import { AfterViewInit, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, of, catchError, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { SearchedPlaceStore } from '../../store/searched-place/searched-place.store';
import { Place } from '../../models/place.interface';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { GEOCODE_API_KEY } from 'src/environments/environment.local';
type ApiPlaceResponse = {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  boundingbox: [number, number, number, number];
  lat: number;
  lon: number;
  display_name: string;
  class: string;
  type: string;
  importance: number;
}

@Component({
  selector: 'app-search-place-bar',
  templateUrl: './search-place-bar.component.html',
  styleUrls: ['./search-place-bar.component.scss'],
  standalone: true,
  imports: [AsyncPipe, TranslateModule, CommonModule],
})
export class SearchPlaceBarComponent implements AfterViewInit {
  @ViewChild('addresstext') addresstext: ElementRef<HTMLInputElement>;

  private http = inject(HttpClient);
  private searchTerms = new Subject<string>();

  searchedPlace$: Observable<Place | null> =
    this.searchedPlaceStore.searchedPlace$;

  suggestions: any[] = [];
  showSuggestions = false;

  constructor(private searchedPlaceStore: SearchedPlaceStore) { }

  ngAfterViewInit(): void {
    this.setupSearch();
    this.setupEnterKeyListener();
  }

  private setupSearch(): void {
    this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term.trim()) {
          return of([]);
        }
        return this.searchPlaces(term);
      })
    ).subscribe(results => {
      this.suggestions = results;
      this.showSuggestions = results.length > 0;
    });

    this.addresstext.nativeElement.addEventListener('input', (event: any) => {
      const value = event.target.value;
      this.searchTerms.next(value);
    });

    this.addresstext.nativeElement.addEventListener('blur', () => {
      // Delay hiding suggestions to allow for clicks
      setTimeout(() => {
        this.showSuggestions = false;
      }, 1000);
    });

    this.addresstext.nativeElement.addEventListener('focus', () => {
      if (this.suggestions.length > 0) {
        this.showSuggestions = true;
      }
    });
  }

  private setupEnterKeyListener(): void {
    this.addresstext.nativeElement.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key === 'Enter' && this.suggestions.length > 0) {
          event.preventDefault();
          this.selectPlace(this.suggestions[0]);
        }
      }
    );
  }

  private searchPlaces(query: string): Observable<ApiPlaceResponse[]> {
    const url = `https://geocode.maps.co/search?q=${encodeURIComponent(query)}&format=json&limit=5&api_key=${GEOCODE_API_KEY}`;
    return this.http.get<ApiPlaceResponse[]>(url).pipe(
      tap(results => console.log('Search results:', results)),
      catchError(error => {
        console.error('Error searching places:', error);
        return of([]);
      })
    );
  }

  selectPlace(result: ApiPlaceResponse): void {
    if (!result) {
      this.searchedPlaceStore.setSearchedPlace(null);
      return;
    }

    const type = this.determinePlaceType(result);
    const place: Place = {
      name: result.display_name,
      location: {
        lat: result.lat,
        lng: result.lon,
      },
      type,
    };

    this.addresstext.nativeElement.value = result.display_name;
    this.searchedPlaceStore.setSearchedPlace(place);
    this.showSuggestions = false;
  }

  private determinePlaceType(result: ApiPlaceResponse): 'country' | 'region' | 'city' {
    const type = result.type?.toLowerCase();
    const addressType = result.type?.toLowerCase();

    if (type === 'country' || addressType === 'country') {
      return 'country';
    } else if (
      type === 'city' ||
      type === 'town' ||
      type === 'village' ||
      addressType === 'city' ||
      addressType === 'town' ||
      addressType === 'village'
    ) {
      return 'city';
    } else {
      return 'region';
    }
  }
}
